import React, { useState } from 'react';
import {
  Modal,
  Form,
  Radio,
  Checkbox,
  Button,
  Space,
  message,
  Progress,
  Typography,
  Divider,
  Input,
} from 'antd';
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;

type FilterMode = 'include' | 'exclude';

interface ExportDialogProps {
  visible: boolean;
  onClose: () => void;
  datasetId: number;
  datasetName?: string;
  selectedImageIds?: number[];
  filters: {
    sizes: string[];
    aspectRatios: string[];
    captionFilters: {
      dropdowns: { [key: string]: string[] };
      booleans: { [key: string]: boolean | null };
      numbers: { [key: string]: { min: number | null; max: number | null } };
      text: { [key: string]: string };
      modes: { [key: string]: FilterMode };
    };
  };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const ExportDialog: React.FC<ExportDialogProps> = ({
  visible,
  onClose,
  datasetId,
  datasetName,
  selectedImageIds,
  filters,
}) => {
  const [format, setFormat] = useState<'json' | 'csv'>('json');
  const [fileName, setFileName] = useState('');
  const [includeFields, setIncludeFields] = useState<string[]>([
    'image_id',
    'r2_url',
    'regular_summary',
    'caption_summary',
    'photo_type',
    'p1_body_type',
    'p1_primary_pose',
  ]);
  const [includeFullData, setIncludeFullData] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const availableFields = [
    { value: 'image_id', label: 'Image ID' },
    { value: 'r2_url', label: 'R2 URL (original)' },
    { value: 'r2_url_thumbnail', label: 'R2 URL (thumbnail)' },
    { value: 'regular_summary', label: 'Parsed Regular Summary' },
    { value: 's3_url', label: 'S3 URL' },
    { value: 'bucket_name', label: 'Bucket Name' },
    { value: 'original_width', label: 'Width' },
    { value: 'original_height', label: 'Height' },
    { value: 'size_category', label: 'Size Category' },
    { value: 'aspect_ratio', label: 'Aspect Ratio' },
    { value: 'caption_summary', label: 'Caption Summary' },
    { value: 'photo_type', label: 'Photo Type' },
    { value: 'framing_focus', label: 'Framing Focus' },
    { value: 'camera_distance', label: 'Camera Distance' },
    { value: 'camera_angle', label: 'Camera Angle' },
    { value: 'p1_estimated_age', label: 'Age' },
    { value: 'p1_body_type', label: 'Body Type' },
    { value: 'p1_apparent_race', label: 'Race' },
    { value: 'p1_gender_identity', label: 'Gender' },
    { value: 'p1_pose_category', label: 'Pose Category' },
    { value: 'p1_primary_pose', label: 'Primary Pose' },
    { value: 'p1_overall_expression', label: 'Expression' },
    { value: 'has_watermark', label: 'Has Watermark' },
    { value: 'poor_quality', label: 'Poor Quality' },
    { value: 'is_selfie', label: 'Is Selfie' },
    { value: 'is_nsfw', label: 'Is NSFW' },
    { value: 'total_people_count', label: 'People Count' },
  ];

  // Checkbox.Group's value array is in click order; export columns should
  // follow the list order shown in the dialog instead.
  const orderedFields = () =>
    availableFields.map(f => f.value).filter(v => includeFields.includes(v));

  // Resolve the download file name: user input (sans extension) or a default
  // derived from the dataset name + date.
  const resolveFileName = (ext: string) => {
    const typed = fileName.trim().replace(/\.(csv|json)$/i, '');
    const base = typed
      || `${(datasetName || `dataset-${datasetId}`)}-export-${new Date().toISOString().slice(0, 10)}`;
    return `${base.replace(/[^a-zA-Z0-9 _.-]/g, '_')}.${ext}`;
  };

  const buildFiltersObject = () => {
    // Return the complete filter state in the format expected by the backend
    return {
      sizes: filters.sizes,
      aspectRatios: filters.aspectRatios,
      dropdowns: filters.captionFilters.dropdowns,
      booleans: filters.captionFilters.booleans,
      numbers: filters.captionFilters.numbers,
      text: filters.captionFilters.text,
      modes: filters.captionFilters.modes,
    };
  };

  const handleExport = async () => {
    setExporting(true);
    setProgress(10);

    try {
      const hasSelection = selectedImageIds && selectedImageIds.length > 0;

      // Use blob response type to handle both small (wrapped JSON) and large (streamed) exports
      const response = await axios.post(`${API_URL}/admin/export`, {
        datasetId,
        imageIds: hasSelection ? selectedImageIds : undefined,
        filters: hasSelection ? undefined : buildFiltersObject(),
        format,
        fields: orderedFields(),
        includeFullData,
      }, {
        responseType: 'blob',
      });

      setProgress(80);

      const contentType = response.headers['content-type'] || '';
      const blob = response.data as Blob;

      // Check if response is a direct file stream or wrapped JSON
      if (contentType.includes('text/csv') || contentType.includes('application/json')) {
        // Large export - direct file stream
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resolveFileName(format === 'csv' ? 'csv' : 'json');
        a.click();
        URL.revokeObjectURL(url);

        setProgress(100);
        message.success('Export completed');
      } else {
        // Small export - wrapped JSON response, parse it
        const text = await blob.text();
        const parsed = JSON.parse(text);
        const { data, count } = parsed;

        if (format === 'json') {
          const jsonBlob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(jsonBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = resolveFileName('json');
          a.click();
          URL.revokeObjectURL(url);
        } else {
          const csvBlob = new Blob([data], { type: 'text/csv' });
          const url = URL.createObjectURL(csvBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = resolveFileName('csv');
          a.click();
          URL.revokeObjectURL(url);
        }

        setProgress(100);
        message.success(`Exported ${count} records`);
      }

      setTimeout(() => {
        onClose();
        setProgress(0);
      }, 500);
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleCopyToClipboard = async () => {
    setExporting(true);

    try {
      // For clipboard, limit to 100 records
      const hasSelection = selectedImageIds && selectedImageIds.length > 0;
      const response = await axios.post(`${API_URL}/admin/export`, {
        datasetId,
        imageIds: hasSelection ? selectedImageIds.slice(0, 100) : undefined,
        filters: hasSelection ? undefined : buildFiltersObject(),
        format: 'json',
        fields: orderedFields(),
        includeFullData,
        limit: hasSelection ? undefined : 100, // Limit filtered results to 100 for clipboard
      });

      const { data, count } = response.data;
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      message.success(`Copied ${count} records to clipboard`);
    } catch (error) {
      console.error('Copy failed:', error);
      message.error('Failed to copy to clipboard');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Modal
      title="Export Data"
      open={visible}
      onCancel={onClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="copy"
          icon={<CopyOutlined />}
          onClick={handleCopyToClipboard}
          loading={exporting}
        >
          Copy (max 100)
        </Button>,
        <Button
          key="export"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          loading={exporting}
        >
          Download
        </Button>,
      ]}
    >
      <div style={{ marginBottom: '16px' }}>
        {selectedImageIds && selectedImageIds.length > 0 ? (
          <Text type="secondary">
            Exporting {selectedImageIds.length} selected images
          </Text>
        ) : (
          <Text type="secondary">
            Exporting all images matching current filters
          </Text>
        )}
      </div>

      <Form layout="vertical">
        <Form.Item label="Format">
          <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)}>
            <Radio.Button value="json">JSON</Radio.Button>
            <Radio.Button value="csv">CSV</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item label="File Name">
          <Input
            placeholder={`${datasetName || `dataset-${datasetId}`}-export-${new Date().toISOString().slice(0, 10)}`}
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            suffix={`.${format}`}
            allowClear
          />
        </Form.Item>

        <Divider />

        <Form.Item label="Fields to Include">
          <Checkbox.Group
            value={includeFields}
            onChange={(values) => setIncludeFields(values as string[])}
          >
            <Space direction="vertical">
              {availableFields.map((field) => (
                <Checkbox key={field.value} value={field.value}>
                  {field.label}
                </Checkbox>
              ))}
            </Space>
          </Checkbox.Group>
        </Form.Item>

        <Form.Item>
          <Checkbox
            checked={includeFullData}
            onChange={(e) => setIncludeFullData(e.target.checked)}
          >
            Include full JSON data (large file size)
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Space>
            <Button size="small" onClick={() => setIncludeFields(availableFields.map(f => f.value))}>
              Select All
            </Button>
            <Button size="small" onClick={() => setIncludeFields([])}>
              Clear All
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {exporting && progress > 0 && (
        <Progress percent={progress} status="active" />
      )}
    </Modal>
  );
};

export default ExportDialog;
